import { useEffect, useState } from "react";
import { Building2, Link2, Unplug } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useGetProviders, useGetUserDetails } from "../../../api/queryHooks";
import {
  disconnectProvider,
  getProviderOAuthUrl,
} from "../../../api/providers";
import UserAuthStore from "../../../store/userStore";
import Loader from "../../Loader/Loader";
import AlertBanner from "../../shared/AlertBanner/AlertBanner";
import {
  getProviderConnectionState,
  PROVIDER_CONNECTION_STATE,
} from "../forgeConnectionState";
import logger from "../../../services/Logger";
import AuthenticatedImage from "../../Common/AuthenticatedImage";

const CONNECTION_LABEL = {
  [PROVIDER_CONNECTION_STATE.READY]: "Ready",
  [PROVIDER_CONNECTION_STATE.ENABLE_REQUIRED]: "Enable required",
  [PROVIDER_CONNECTION_STATE.CONNECTED]: "Connected",
  [PROVIDER_CONNECTION_STATE.REAUTHENTICATION_REQUIRED]: "Reconnect required",
  [PROVIDER_CONNECTION_STATE.DISCONNECTED]: "Not connected",
};

const ProviderLogo = ({ provider, orgId }) => {
  return (
    <div className="w-10 h-10 rounded-lg border border-edge-gray-200 dark:border-edge-panel-border bg-fill-white dark:bg-fill-deep-surface flex items-center justify-center flex-shrink-0 overflow-hidden">
      <AuthenticatedImage
        filePath={provider.icon_path}
        orgId={orgId}
        alt=""
        className="w-7 h-7 object-contain"
        fallback={<Building2 className="w-5 h-5 text-ink-gray-500 dark:text-ink-gray-400" />}
      />
    </div>
  );
};

const ProviderConnectionsSection = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = UserAuthStore((state) => state.user);
  const orgId = UserAuthStore((state) => state.orgId);
  const { data: providers = [], isLoading: providersLoading } = useGetProviders(orgId);
  const { data: userDetails, isLoading: userLoading } = useGetUserDetails(orgId, user?.uid);
  const [disconnectingId, setDisconnectingId] = useState(null);
  const [error, setError] = useState(null);
  const [oauthResult] = useState(() => ({
    status: searchParams.get("oauth"),
    providerId: searchParams.get("provider"),
  }));

  useEffect(() => {
    if (!oauthResult.status) return;

    if (oauthResult.status === "success") {
      queryClient.invalidateQueries({ queryKey: ["user-details", user?.uid, orgId] });
    }
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete("oauth");
      next.delete("provider");
      return next;
    }, { replace: true });
  }, [oauthResult.status, orgId, queryClient, setSearchParams, user?.uid]);

  const connect = async (provider) => {
    if (!user?.uid || !orgId) return;
    const authorizationUrl = await getProviderOAuthUrl(provider.provider_id, user.uid, orgId, {
      returnTo: "forge_connections",
    });
    window.location.href = authorizationUrl;
  };

  const disconnect = async (provider) => {
    setDisconnectingId(provider.provider_id);
    setError(null);
    try {
      await disconnectProvider(provider.provider_id, user.uid, orgId);
      await queryClient.invalidateQueries({ queryKey: ["user-details", user.uid, orgId] });
    } catch (err) {
      logger.error("ProviderConnectionsSection", "Failed to disconnect provider", {
        error: err.message,
        providerId: provider.provider_id,
      });
      setError(err.response?.data?.detail || "Failed to disconnect provider.");
    } finally {
      setDisconnectingId(null);
    }
  };

  if (providersLoading || userLoading) {
    return <Loader variant="dashboard" />;
  }

  const availableProviders = providers.filter((provider) => provider.enabled !== false);
  const connectedProvider = providers.find(
    (provider) => provider.provider_id === oauthResult.providerId
  );

  return (
    <div>
      {oauthResult.status === "success" && (
        <AlertBanner variant="success" className="mb-5">
          {connectedProvider?.name || "Provider"} connected.
        </AlertBanner>
      )}
      {oauthResult.status === "error" && (
        <AlertBanner className="mb-5">
          Provider connection was not completed. Try again when you are ready.
        </AlertBanner>
      )}
      {error && <AlertBanner className="mb-5">{error}</AlertBanner>}

      {availableProviders.length === 0 ? (
        <div className="border border-dashed border-edge-gray-300 dark:border-edge-panel-border rounded-lg px-6 py-14 text-center">
          <Link2 className="w-8 h-8 mx-auto text-ink-gray-400 mb-3" />
          <p className="text-sm font-semibold text-ink-gray-900 dark:text-ink-gray-50">No connections available</p>
          <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400 mt-1">Your administrator has not enabled any Forge providers.</p>
        </div>
      ) : (
        <div className="border border-edge-gray-200 dark:border-edge-panel-border rounded-lg overflow-hidden">
          {availableProviders.map((provider, index) => {
            const connectionState = getProviderConnectionState(userDetails, provider);
            const native = provider.service_type === "native";
            const connected = connectionState === PROVIDER_CONNECTION_STATE.CONNECTED;
            const ready = connectionState === PROVIDER_CONNECTION_STATE.READY;
            const reconnect = connectionState === PROVIDER_CONNECTION_STATE.REAUTHENTICATION_REQUIRED;

            return (
              <div
                key={provider.provider_id}
                className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-5 py-4 bg-fill-white dark:bg-fill-dialog-surface ${index > 0 ? "border-t border-edge-gray-200 dark:border-edge-panel-border" : ""}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ProviderLogo provider={provider} orgId={orgId} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink-gray-900 dark:text-ink-gray-50 truncate">{provider.name}</p>
                    <p className="text-[11px] text-ink-gray-500 dark:text-ink-gray-400 mt-0.5">
                      {provider.service_type === "native" && connectionState === PROVIDER_CONNECTION_STATE.ENABLE_REQUIRED
                        ? "Applies Gluon Link governance. No external sign-in is required."
                        : provider.service_type === "native" ? "Built in" : "Personal account connection"}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-4">
                  <span className={`text-xs font-medium ${connected || ready ? "text-ink-emerald-700 dark:text-ink-emerald-400" : reconnect ? "text-ink-amber-700 dark:text-ink-amber-400" : "text-ink-gray-500 dark:text-ink-gray-400"}`}>
                    {native && connected ? "Enabled" : CONNECTION_LABEL[connectionState]}
                  </span>

                  {!ready && (
                    connected ? (
                      <button
                        type="button"
                        onClick={() => disconnect(provider)}
                        disabled={disconnectingId === provider.provider_id}
                        className="inline-flex items-center gap-1.5 rounded-md border border-edge-gray-300 dark:border-edge-gray-600 px-3 py-1.5 text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300 hover:bg-fill-gray-50 dark:hover:bg-fill-dialog-border disabled:opacity-50"
                      >
                        <Unplug className="w-3.5 h-3.5" />
                        {disconnectingId === provider.provider_id
                          ? native ? "Disabling..." : "Disconnecting..."
                          : native ? "Disable" : "Disconnect"}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => connect(provider)}
                        className="inline-flex items-center gap-1.5 rounded-md bg-fill-primary px-3 py-1.5 text-xs font-medium text-ink-on-primary hover:bg-fill-primary-hover"
                      >
                        <Link2 className="w-3.5 h-3.5" />
                        {reconnect
                          ? "Reconnect"
                          : connectionState === PROVIDER_CONNECTION_STATE.ENABLE_REQUIRED ? "Enable" : "Connect"}
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProviderConnectionsSection;
